/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import KeyboardShortcut from "shortcutManager/KeyboardShortcut";
import { formatShortcutExpr, getFormattedShortcutExpr, getDisplayLabel } from "shortcutManager/KeyboardShortcutUtils";
import { errorCodes } from "shortcutManager/constants";

export default class KeyboardShortcuts {
  /*
   * Key Expression Format: `alt_ctrl_meta_shift_code`
   */

  constructor() {
    this.shortcutsById = new Map();
    this.idsByShortcutExpr = new Map();
  }

  addShortcut = ({ id, shortcutExprs, handler }) => {
    if (!(id && shortcutExprs && shortcutExprs.length > 0)) return { success: false, error: errorCodes.INVALID_DATA };

    if (this.shortcutsById.has(id)) {
      return {
        success: false,
        error: errorCodes.DUPLICATE_ID,
        conflictingId: id
      };
    }

    const formattedExprs = formatShortcutExpr ? shortcutExprs.map(formatShortcutExpr) : [];
    for (const formattedExpr of formattedExprs) {
      if (!formattedExpr) return { success: false, error: errorCodes.INVALID_DATA };

      const conflictingId = this.idsByShortcutExpr.get(formattedExpr);
      if (conflictingId) {
        return {
          success: false,
          error: errorCodes.DUPLICATE_SHORTCUT,
          conflictingId
        };
      }
    }

    formattedExprs.forEach(formattedExpr => {
      this.idsByShortcutExpr.set(formattedExpr, id);
    });
    this.shortcutsById.set(id, new KeyboardShortcut(id, shortcutExprs, handler));
    return { success: true };
  };

  removeShortcut = id => {
    const shortcut = this.shortcutsById.get(id);
    if (shortcut) {
      shortcut.shortcutExprs.forEach(shortcutExpr => {
        const formattedShortcutExpr = formatShortcutExpr(shortcutExpr);
        this.idsByShortcutExpr.delete(formattedShortcutExpr);
      });

      this.shortcutsById.delete(id);
    }
  };

  getShortcut = id => this.shortcutsById.get(id);

  getAllShortcuts = () => Array.from(this.shortcutsById.values());

  registerShortcutHandler = (id, handler) => {
    const shortcut = this.getShortcut(id);
    if (shortcut) {
      shortcut.handler = handler;
    }
  };

  unregisterShortcutHandler = id => {
    const shortcut = this.getShortcut(id);
    if (shortcut) {
      shortcut.handler = null;
    }
  };

  getShortcutDisplayLabel = id => {
    let displayLabel;
    const shortcut = this.getShortcut(id);
    if (shortcut) {
      displayLabel = shortcut.shortcutExprs[0]
        .split("+")
        .map(getDisplayLabel)
        .join(" ");
    }
    return displayLabel;
  };

  getHandler = event => {
    const formattedShortcutExpr = getFormattedShortcutExpr(event);
    const id = this.idsByShortcutExpr.get(formattedShortcutExpr);
    return id ? this.shortcutsById.get(id).handler : null;
  };

  reset = () => {
    this.shortcutsById.clear();
    this.idsByShortcutExpr.clear();
  };
}
